import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';
import { useToast } from '../components/Toaster.tsx';

type SkillLevel = 'prime' | 'second' | 'learn';

interface AgentLite {
  id: string;
  name: string;
  email: string;
  role: 'agent' | 'admin' | 'time_admin';
  isFullTime: boolean;
  phoneNumber: string | null;
}

interface SkillLite {
  id: string;
  name: string;
  displayOrder: number;
  isActive: boolean;
}

interface MatrixCell {
  agentId: string;
  skillId: string;
  level: SkillLevel;
}

interface MatrixPayload {
  agents: AgentLite[];
  skills: SkillLite[];
  cells: MatrixCell[];
}

// Click-to-cycle order for the matrix editor: empty → Prime → Second →
// Learn → back to empty. Lets the admin fill an entire row with single
// clicks rather than opening a dropdown for each cell.
const NEXT_LEVEL: Record<SkillLevel | 'empty', SkillLevel | null> = {
  empty: 'prime',
  prime: 'second',
  second: 'learn',
  learn: null,
};

const LEVEL_LABEL: Record<SkillLevel, string> = {
  prime: 'Prime',
  second: 'Second',
  learn: 'Learn',
};

// One-stop admin matrix editor. Mirrors the original spreadsheet layout
// (rows = skills, columns = agents). Click any cell to cycle the level.
// The read-only directory at /team is the reverse layout (skill-first
// lookup) — this page is where admins fill the data; the directory is
// where techs read it.
export function AdminSkillsMatrixPage(): React.ReactElement {
  useDocumentTitle('Skills matrix · admin');
  const { agent: me } = useAuth();
  const toast = useToast();
  const [data, setData] = useState<MatrixPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newSkillName, setNewSkillName] = useState('');
  const [busy, setBusy] = useState(false);

  function reload(): void {
    apiFetch<MatrixPayload>('/api/agents/matrix')
      .then(setData)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Failed to load.'));
  }

  useEffect(() => {
    reload();
  }, []);

  // Lookup keyed "<agentId>|<skillId>" for O(1) cell access in the table render.
  const cellMap = useMemo(() => {
    const m = new Map<string, SkillLevel>();
    for (const c of data?.cells ?? []) m.set(`${c.agentId}|${c.skillId}`, c.level);
    return m;
  }, [data]);

  async function cycleCell(agentId: string, skillId: string): Promise<void> {
    const current = cellMap.get(`${agentId}|${skillId}`) ?? 'empty';
    const next = NEXT_LEVEL[current];
    try {
      await apiFetch(`/api/agents/${agentId}/skills/${skillId}`, {
        method: 'PUT',
        json: { level: next },
      });
      // Optimistic update so the matrix feels instant.
      setData((prev) => {
        if (!prev) return prev;
        const without = prev.cells.filter((c) => !(c.agentId === agentId && c.skillId === skillId));
        return {
          ...prev,
          cells: next ? [...without, { agentId, skillId, level: next }] : without,
        };
      });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to update skill.');
    }
  }

  async function addSkill(): Promise<void> {
    const name = newSkillName.trim();
    if (!name) return;
    const maxOrder = data?.skills.reduce((m, s) => Math.max(m, s.displayOrder), 0) ?? 0;
    setBusy(true);
    try {
      await apiFetch('/api/skills', {
        method: 'POST',
        json: { name, displayOrder: maxOrder + 10 },
      });
      toast.success(`Added "${name}".`);
      setNewSkillName('');
      setAdding(false);
      reload();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to add skill.');
    } finally {
      setBusy(false);
    }
  }

  if (!me || me.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }
  if (error) return <Card><p className="text-sm text-[var(--color-danger)]">{error}</p></Card>;
  if (!data) return <p className="text-[var(--color-muted)]">Loading…</p>;

  return (
    <div className="space-y-6">
      <header>
        <Link to="/admin" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Admin
        </Link>
        <h1 className="mt-1 text-3xl font-bold text-[var(--color-header)]">Skills matrix</h1>
        <p className="mt-1 text-[var(--color-muted)]">
          Fill in everyone's ratings in one place. Click a cell to cycle the level:{' '}
          <Chip level="prime">P</Chip> Prime → <Chip level="second">S</Chip> Second →{' '}
          <Chip level="learn">L</Chip> Learn → empty. Read-only directory lives at{' '}
          <Link to="/team" className="text-[var(--color-t3-blue)] hover:underline">/team</Link>.
        </p>
      </header>

      <Card>
        <div className="flex flex-wrap items-center gap-2">
          {!adding ? (
            <Button variant="outline" onClick={() => setAdding(true)}>
              + Add skill
            </Button>
          ) : (
            <>
              <input
                type="text"
                autoFocus
                placeholder="New skill name (e.g. Intune, Fortinet, SQL Server…)"
                value={newSkillName}
                onChange={(e) => setNewSkillName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void addSkill();
                  else if (e.key === 'Escape') {
                    setAdding(false);
                    setNewSkillName('');
                  }
                }}
                maxLength={100}
                className="h-9 min-w-[280px] flex-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 text-sm"
              />
              <Button onClick={() => void addSkill()} disabled={busy || !newSkillName.trim()}>
                {busy ? 'Adding…' : 'Add'}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => {
                  setAdding(false);
                  setNewSkillName('');
                }}
              >
                Cancel
              </Button>
            </>
          )}
          <span className="ml-auto text-xs text-[var(--color-muted)]">
            {data.skills.length} skills · {data.agents.length} active techs
          </span>
        </div>
      </Card>

      <Card className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-max text-sm">
            <thead>
              <tr className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                <th className="sticky left-0 z-10 min-w-[260px] bg-[var(--color-surface)] px-4 py-3 font-semibold">
                  Skill
                </th>
                {data.agents.map((a) => (
                  <th key={a.id} className="whitespace-nowrap px-3 py-3 text-center">
                    <Link
                      to={`/agents/${a.id}`}
                      className="font-semibold text-[var(--color-t3-blue)] hover:underline"
                      title="Open profile"
                    >
                      {a.name}
                    </Link>
                    {!a.isFullTime && (
                      <div className="mt-0.5 text-[10px] font-normal normal-case text-amber-700">
                        Part-time
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.skills.map((s, i) => (
                <tr
                  key={s.id}
                  className={
                    'border-t border-[var(--color-border)] ' +
                    (i % 2 === 0 ? 'bg-white' : 'bg-[var(--color-surface)]/30')
                  }
                >
                  <td className="sticky left-0 z-10 bg-inherit px-4 py-2 font-medium text-[var(--color-header)]">
                    {s.name}
                  </td>
                  {data.agents.map((a) => {
                    const level = cellMap.get(`${a.id}|${s.id}`) ?? null;
                    return (
                      <td key={a.id} className="px-2 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => void cycleCell(a.id, s.id)}
                          className={
                            'inline-flex h-7 w-16 cursor-pointer items-center justify-center rounded-full text-xs font-semibold transition hover:brightness-95 ' +
                            (level === 'prime'
                              ? 'bg-[var(--color-t3-blue)] text-white'
                              : level === 'second'
                                ? 'bg-[var(--color-t3-purple)] text-white'
                                : level === 'learn'
                                  ? 'bg-amber-100 text-amber-900 border border-amber-300'
                                  : 'bg-transparent text-[var(--color-muted)] border border-dashed border-[var(--color-border)]')
                          }
                          title={
                            level
                              ? `${a.name} — ${LEVEL_LABEL[level]} (click to step down)`
                              : `${a.name} — click to set Prime`
                          }
                        >
                          {level ? LEVEL_LABEL[level] : '—'}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="flex flex-wrap items-center gap-4 text-xs text-[var(--color-muted)]">
        <span className="inline-flex items-center gap-1.5">
          <Chip level="prime">P</Chip> Prime — primary skill
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Chip level="second">S</Chip> Second — capable backup
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Chip level="learn">L</Chip> Learn — actively skilling up
        </span>
      </div>
    </div>
  );
}

function Chip({ level, children }: { level: SkillLevel; children: React.ReactNode }): React.ReactElement {
  const cls =
    level === 'prime'
      ? 'bg-[var(--color-t3-blue)] text-white'
      : level === 'second'
        ? 'bg-[var(--color-t3-purple)] text-white'
        : 'bg-amber-100 text-amber-900 border border-amber-300';
  return (
    <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold ${cls}`}>
      {children}
    </span>
  );
}
